import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   Job Costs — shared token components + helpers (warm-cream + sage)
   Used by both the top-level Job Costs dashboard (JobCostDashboardPage) and the
   project-detail Job Costs tab (JobCostTab). Consumes normalized shapes built
   by each page from the real CE-sync endpoints.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─────────────── money + format ─────────────── */

export const fmt$ = (n, { signed = false } = {}) => {
  if (n == null || Number.isNaN(+n)) return '—';
  const num = +n;
  const abs = Math.abs(num).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (num < 0) return '-' + abs;
  if (signed && num > 0) return '+' + abs;
  return abs;
};

export const fmt$c = (n, { signed = false } = {}) => {
  if (n == null || Number.isNaN(+n)) return '—';
  const num = +n;
  const a = Math.abs(num);
  let s;
  if (a >= 1e6) { const m = a / 1e6; s = '$' + (m >= 100 ? m.toFixed(0) : m.toFixed(1)) + 'M'; }
  else if (a >= 1e3) s = '$' + Math.round(a / 1e3) + 'k';
  else s = '$' + Math.round(a);
  if (num < 0) return '-' + s;
  if (signed && num > 0) return '+' + s;
  return s;
};

export const fmtPct = (n, dp = 1) => (n == null || Number.isNaN(+n) ? '—' : (Math.round(+n * Math.pow(10, dp)) / Math.pow(10, dp)).toFixed(dp) + '%');

export const fmtDate = (d) => {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const ring = (c) => `color-mix(in oklch, ${c} 22%, transparent)`;

const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ─────────────── cost-health ramp (pct = cost/budget, RATIO 0–1) ─────────────── */

// Cost-health tiers. These thresholds are the ONE source of truth for both the
// health badge AND the dashboard filter (filterBucket derives from this below),
// so a job's badge can never disagree with the bucket the filter drops it into.
// Strict boundaries at exactly-100%: anything over (even a penny) is Over Budget,
// EXACTLY at budget (cost === budget → pct 1.0) is a calm neutral "At Budget",
// and anything under (even 99.9999%) is At Risk down to 85%. x/x === 1.0 exactly
// in IEEE754, so assumed-at-budget items (cost set = budget) and any real line
// where cost equals budget both land cleanly in 'atbudget'.
//   over >100% · at-budget =100% · at-risk 85–<100% · on-track 50–<85% · under <50%
export const healthKey = (pct) => {
  if (pct == null) return 'none';
  if (pct > 1) return 'over';          // strictly over budget → Over Budget
  if (pct >= 1) return 'atbudget';     // exactly at budget (pct === 1) → At Budget (neutral)
  if (pct >= 0.85) return 'at';        // 85–<100% → At Risk
  if (pct >= 0.5) return 'on';         // 50–<85%  → On Track
  return 'under';                      // <50%     → Under Budget
};
const HEALTH = {
  over: { label: 'Over Budget', tone: 'var(--danger)' },
  atbudget: { label: 'At Budget', tone: 'var(--text-3)' },
  at: { label: 'At Risk', tone: 'var(--warning)' },
  on: { label: 'On Track', tone: 'var(--success)' },
  under: { label: 'Under Budget', tone: 'var(--success)' },
  none: { label: 'No Budget', tone: 'var(--text-4)' },
};
export const healthMeta = (pct) => ({ key: healthKey(pct), ...HEALTH[healthKey(pct)] });
export const healthTone = (pct) => HEALTH[healthKey(pct)].tone;
export const spentColor = (pct) => {
  const k = healthKey(pct);
  if (k === 'none') return 'var(--text-4)';
  if (k === 'under' || k === 'atbudget') return 'var(--text-1)';
  return HEALTH[k].tone;
};
const healthBadgeColors = (pct) => {
  const k = healthKey(pct);
  if (k === 'over') return { fg: 'var(--danger)', soft: 'var(--danger-soft)', ringTone: 'var(--danger)' };
  if (k === 'at') return { fg: 'var(--warning)', soft: 'var(--warning-soft)', ringTone: 'var(--warning)' };
  if (k === 'on' || k === 'under') return { fg: 'var(--success)', soft: 'var(--success-soft)', ringTone: 'var(--success)' };
  return { fg: 'var(--text-3)', soft: 'var(--bg-surface-2)', ringTone: 'var(--border-strong)' };
};
export const overTone = (v) => (v > 0.5 ? 'var(--danger)' : v < -0.5 ? 'var(--success)' : 'var(--text-3)');
// Dashboard filter buckets — derived from healthKey so the filter and the badge
// can never drift apart: over (>100%), at-risk (85–100%, incl. exactly-at-budget),
// on-track (<85%). (No job is ever exactly at budget, so 'atbudget' here is a
// near-dead path — grouped with at-risk as the fully-consumed end.)
export const filterBucket = (pct) => {
  const k = healthKey(pct);
  if (k === 'over') return 'over';
  if (k === 'atbudget' || k === 'at') return 'at-risk';
  return 'on-track'; // on, under, none
};

/* ─────────────── assumed-at-budget cost codes ─────────────── */

export const ASSUMED_AT_BUDGET_CODES = new Set(['910', '911', '912', '913', '914', '915', '916', '917', '918', '391', '362']);
export const applyBudgetAssumption = (items) =>
  (items || []).map((li) => {
    const cat = String(li.category || '').trim();
    if (ASSUMED_AT_BUDGET_CODES.has(cat)) {
      const budget = parseFloat(li.cost_budget || 0);
      return { ...li, cost: budget, cost_overrun: 0, _assumed: true };
    }
    return li;
  });

/* ─────────────── normalizers: real CE shapes → table/chart shapes ─────────────── */

// rawPhases: [{phase, phase_name, total_budget, total_cost, total_committed, total_overrun}]
// adjLineItems: applyBudgetAssumption() output [{phase, category, category_name, cost_budget, cost, cost_committed, cost_overrun, _assumed}]
export const buildPhases = (rawPhases, adjLineItems) => {
  const nonZero = (v) => parseFloat(v || 0) !== 0;
  return (rawPhases || [])
    .map((p) => {
      const items = (adjLineItems || []).filter((li) => li.phase === p.phase);
      const codes = items
        .filter((li) => nonZero(li.cost_budget) || nonZero(li.cost) || nonZero(li.cost_committed) || nonZero(li.cost_overrun))
        .map((li) => ({
          cat: li.category_name || li.category || '—',
          code: li.category || '',
          budget: parseFloat(li.cost_budget || 0),
          actual_cost: parseFloat(li.cost || 0),
          committed: parseFloat(li.cost_committed || 0),
          over_under: parseFloat(li.cost_overrun || 0),
          assumed: !!li._assumed,
        }));
      // recompute phase totals from adjusted items when present
      const sum = (k) => codes.reduce((s, c) => s + c[k], 0);
      const budget = codes.length ? sum('budget') : parseFloat(p.total_budget || 0);
      const actual_cost = codes.length ? sum('actual_cost') : parseFloat(p.total_cost || 0);
      const committed = codes.length ? sum('committed') : parseFloat(p.total_committed || 0);
      const over_under = codes.length ? actual_cost - budget : parseFloat(p.total_overrun || 0);
      return { no: p.phase, name: p.phase_name || `Phase ${p.phase}`, budget, actual_cost, committed, over_under, codes };
    })
    .filter((p) => p.budget !== 0 || p.actual_cost !== 0 || p.committed !== 0 || p.over_under !== 0);
};

// rows: [{period_month (1-12), period_year, total_cost}]
// total_cost is CUMULATIVE (inception-to-date through that month). The chart shows
// per-month SPEND, so we difference each month from the prior month's cumulative
// (first month's spend = its own cumulative — nothing earlier to subtract). This
// makes the bars sum exactly to the current cumulative total. Sorted defensively
// so the differencing is correct regardless of row order. `cumulative` is kept for
// tooltips / a future cumulative toggle.
export const buildMonthly = (rows) => {
  const src = [...(rows || [])].sort(
    (a, b) => (a.period_year - b.period_year) || (a.period_month - b.period_month)
  );
  return src.map((m, i) => {
    const cumulative = parseFloat(m.total_cost || 0);
    const prev = i > 0 ? parseFloat(src[i - 1].total_cost || 0) : 0;
    return {
      month: `${MONTH_ABBR[+m.period_month] || ''} '${String(m.period_year).slice(-2)}`,
      cost: cumulative - prev, // per-month spend (bars sum to the total)
      cumulative,
    };
  });
};

/* ─────────────── inline SVG icons ─────────────── */

export const JcIcon = ({ name, size = 16, sw = 1.6 }) => {
  const s = size;
  const p = (d) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      {d}
    </svg>
  );
  switch (name) {
    case 'refresh':
      return p(<><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" /><path d="M3 21v-5h5" /></>);
    case 'x':
      return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'chevron-d':
      return p(<path d="m6 9 6 6 6-6" />);
    case 'archive':
      return p(<><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" /><path d="M10 12h4" /></>);
    case 'external':
      return p(<><path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" /></>);
    case 'note':
      return p(<><path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2Z" /><path d="M8 9h8M8 13h5" /></>);
    case 'note-plus':
      return p(<><path d="M21 11.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v15l4-4h6" /><path d="M18 14v6M15 17h6" /></>);
    case 'sync':
      return p(<><path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3" /><path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3" /><path d="M21 3v5h-5M3 21v-5h5" /></>);
    default:
      return null;
  }
};

/* ─────────────── health badge ─────────────── */

export const HealthBadge = ({ pct, size = 'md' }) => {
  const b = healthBadgeColors(pct);
  const h = healthMeta(pct);
  const sm = size === 'sm';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: sm ? 19 : 22, padding: sm ? '0 8px' : '0 10px', borderRadius: 999, fontSize: sm ? 10.5 : 11.5, fontWeight: 600, letterSpacing: '-0.005em', whiteSpace: 'nowrap', color: b.fg, background: b.soft, border: `1px solid ${ring(b.ringTone)}` }}>
      <span style={{ width: 6, height: 6, borderRadius: 99, background: b.fg, flex: '0 0 auto' }} />
      {h.label}
    </span>
  );
};

/* ─────────────── detailed KPI cards (two rows of 4) ─────────────── */

const KpiCard = ({ label, value, sub, valueColor, fill, ringColor, muted }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, padding: '14px 16px', borderRadius: 10, background: fill || 'var(--bg-surface)', border: `1px solid ${ringColor || 'var(--border-default)'}`, boxShadow: 'var(--shadow-xs)' }}>
    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.055em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    <div className="mono" style={{ fontSize: 22.5, fontWeight: 800, lineHeight: 1.05, color: muted ? 'var(--text-4)' : valueColor || 'var(--text-1)', letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub && <div className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
  </div>
);

const signTone = (v) => (v >= 0 ? 'var(--success)' : 'var(--danger)');

export const KpiCards = ({ fin }) => {
  const f = fin;
  const gmDelta = f.actual_gm_pct - f.gm_target;   // actual GM% − target GM%
  const gmDollarDelta = f.actual_gm - f.gm;        // actual GM$ − target GM$
  // "Quiet ledger" restraint: neutral surface cards (no soft-fills); color is
  // reserved for the values that carry meaning (margins + spend health). The two
  // Gross Margin tiles LEAD with the ACTUAL (current reality, colored by sign)
  // and carry the TARGET + delta-vs-target on the sub-line — so one tile shows
  // goal-vs-actual at a glance (was four separate Target/Actual GM tiles). Actual
  // Cost folds Budget in (Budget + delta on the sub); Risk + Travel show spent of
  // budget. 7 tiles in a 4-col grid (last row of 3). Cost/Risk/Travel go red over budget.
  // Order per Duncan (2026-07-12), L→R top row first:
  // Contract Value · Actual Cost (vs budget) · GM % · Travel  //  Billing % · GM $ · Risk.
  const cards = [
    { label: 'Contract Value', value: fmt$(f.contract_value) },
    {
      label: 'Actual Cost',
      value: fmt$(f.actual_cost),
      valueColor: spentColor(f.pctSpent),
      sub: (
        <>Budget {fmt$(f.budget)} · <span style={{ color: f.over_under > 0 ? 'var(--danger)' : 'var(--success)' }}>{fmt$(f.over_under, { signed: true })}</span></>
      ),
    },
    {
      label: 'Gross Margin %',
      value: fmtPct(f.actual_gm_pct * 100),
      valueColor: signTone(f.actual_gm),
      sub: (
        <>Target {fmtPct(f.gmPct * 100)} · <span style={{ color: signTone(gmDelta) }}>{`${gmDelta >= 0 ? '+' : ''}${fmtPct(gmDelta * 100)}`}</span></>
      ),
    },
    {
      label: 'Travel',
      value: fmt$(f.travel_cost),
      valueColor: f.travel_budget > 0 && f.travel_cost > f.travel_budget ? 'var(--danger)' : undefined,
      sub: `of ${fmt$(f.travel_budget)} budget`,
    },
    { label: 'Billing % Complete', value: fmtPct(f.billing_pct * 100), sub: `${fmt$(f.billed)} billed` },
    {
      label: 'Gross Margin $',
      value: fmt$(f.actual_gm, { signed: true }),
      valueColor: signTone(f.actual_gm),
      sub: (
        <>Target {fmt$(f.gm)} · <span style={{ color: signTone(gmDollarDelta) }}>{fmt$(gmDollarDelta, { signed: true })}</span></>
      ),
    },
    {
      label: 'Risk',
      value: fmt$(f.risk_cost),
      valueColor: f.risk_budget > 0 && f.risk_cost > f.risk_budget ? 'var(--danger)' : undefined,
      sub: `of ${fmt$(f.risk_budget)} budget`,
    },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
      {cards.map((c) => <KpiCard key={c.label} {...c} />)}
    </div>
  );
};

/* ─────────────── Monthly Cost Trend — sage SVG bar chart ─────────────── */

export const MonthlyChart = ({ monthly }) => {
  const wrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setWrapW(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  if (!monthly || monthly.length === 0) return <div style={{ padding: 16, textAlign: 'center', fontSize: 13.5, color: 'var(--text-4)' }}>No monthly cost data.</div>;

  const H = 220, padL = 16, padR = 56, padT = 22, padB = 30;
  const plotH = H - padT - padB;
  const n = monthly.length || 1;
  // Responsive: fill the container width, but never let a month get narrower
  // than MIN_STEP (labels collide below ~50px). With many months this grows the
  // SVG past the container so the wrapper scrolls; with few it spreads to fill.
  const MIN_STEP = 50;
  const plotArea = Math.max((wrapW || 0) - padL - padR, 120);
  const step = Math.max(plotArea / n, MIN_STEP);
  const plotW = step * n;
  const W = plotW + padL + padR;
  const barW = Math.min(60, step * 0.5);
  const rawMax = Math.max(...monthly.map((d) => d.cost), 1);
  const niceMax = Math.ceil(rawMax / 1e5) * 1e5 || 1e5;
  const cx = (i) => padL + step * (i + 0.5);
  const by = (v) => padT + plotH - (v / niceMax) * plotH;
  const grid = [0, 1, 2, 3, 4];
  return (
    <div ref={wrapRef} style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        <defs>
          <linearGradient id="jcBar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="color-mix(in oklch, var(--accent) 78%, transparent)" />
            <stop offset="100%" stopColor="color-mix(in oklch, var(--accent) 40%, transparent)" />
          </linearGradient>
        </defs>
        {grid.map((g) => {
          const y = padT + (plotH * g) / 4;
          const v = niceMax * (1 - g / 4);
          return (
            <g key={g}>
              <line x1={padL} y1={y} x2={padL + plotW} y2={y} stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray={g === 4 ? '0' : '2 3'} />
              <text x={padL + plotW + 8} y={y + 3.5} className="mono" fontSize="9.5" fill="var(--text-4)" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt$c(v)}</text>
            </g>
          );
        })}
        {monthly.map((d, i) => {
          const h = (d.cost / niceMax) * plotH, x = cx(i) - barW / 2, y = by(d.cost);
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={Math.max(0, h)} rx="3" fill="url(#jcBar)" stroke={ring('var(--accent)')} strokeWidth="1" />
              <text x={cx(i)} y={y - 6} textAnchor="middle" className="mono" fontSize="10" fontWeight="600" fill="var(--accent-text)" style={{ fontVariantNumeric: 'tabular-nums' }}>{d.cost > 0 ? fmt$c(d.cost) : ''}</text>
              <text x={cx(i)} y={H - 10} textAnchor="middle" fontSize="10.5" fill="var(--text-3)">{d.month}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/* ─────────────── note indicator + popover ─────────────── */

const NoteIndicator = ({ notes, has, onClick }) => (
  <button
    type="button"
    className={`j-note${has ? ' has' : ''}`}
    onClick={onClick}
    title={has ? `${notes.length} note${notes.length === 1 ? '' : 's'}` : 'Add a note'}
    style={{ width: 26, height: 26, borderRadius: 7, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: has ? 'var(--accent-text)' : 'var(--text-4)', background: has ? 'var(--accent-soft)' : 'transparent', border: `1px solid ${has ? ring('var(--accent)') : 'transparent'}`, position: 'relative' }}
  >
    <JcIcon name={has ? 'note' : 'note-plus'} size={14} sw={1.7} />
    {has && (
      <span className="mono" style={{ position: 'absolute', top: -5, right: -5, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 999, background: 'var(--accent)', color: '#fff', fontSize: 10.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid var(--bg-surface)' }}>{notes.length}</span>
    )}
  </button>
);

const NotePopover = ({ notes, pos, codeLabel, onAdd, onClose }) => {
  const [draft, setDraft] = useState('');
  const taRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (taRef.current) taRef.current.focus(); }, []);
  const submit = async () => {
    const txt = draft.trim();
    if (!txt || submitting) return;
    setSubmitting(true);
    try {
      await onAdd(txt);
      setDraft('');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: pos.top, right: pos.right, zIndex: 40, width: 320, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
        <span style={{ color: 'var(--accent-text)', display: 'inline-flex' }}><JcIcon name="note" size={14} /></span>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{codeLabel}</span>
        <button type="button" onClick={onClose} style={{ width: 22, height: 22, borderRadius: 6, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}>
          <JcIcon name="x" size={13} sw={2} />
        </button>
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto', padding: notes.length ? '4px 0' : 0 }}>
        {notes.length === 0 ? (
          <div style={{ padding: '18px 14px', fontSize: 14, color: 'var(--text-3)', textAlign: 'center' }}>No notes yet on this cost code.</div>
        ) : (
          notes.map((nt, i) => (
            <div key={i} style={{ padding: '9px 13px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{nt.author}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto', whiteSpace: 'nowrap' }}>{fmtDate(nt.date)}</span>
              </div>
              <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5 }}>{nt.text}</div>
            </div>
          ))
        )}
      </div>
      <div style={{ padding: 10, borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
          placeholder="Add a note…"
          rows={2}
          className="j-note-ta"
          style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 46, padding: '8px 10px', borderRadius: 8, fontFamily: 'var(--font-sans)', fontSize: 14, color: 'var(--text-1)', lineHeight: 1.5, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', outline: 'none' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || submitting}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 13px', borderRadius: 7, fontSize: 14, fontWeight: 600, color: '#fff', background: draft.trim() ? 'var(--accent)' : 'color-mix(in oklch, var(--accent) 45%, var(--bg-surface-2))', border: '1px solid color-mix(in oklch, var(--accent) 50%, oklch(0.3 0.05 135))', boxShadow: draft.trim() ? '0 1px 0 oklch(0.42 0.06 135)' : 'none', cursor: draft.trim() && !submitting ? 'pointer' : 'default' }}
          >
            <JcIcon name="note-plus" size={13} sw={1.9} /> {submitting ? 'Adding…' : 'Add note'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── Phase Breakdown table ─────────────── */

const GRID = '2fr 1fr 1fr 1fr 1fr 1fr 44px';

const Cell = ({ children, col, colHover, align = 'right', style = {} }) => {
  const tinted = col >= 1 && col <= 5 && col === colHover;
  return (
    <div data-col={col} style={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start', padding: '0 12px', minWidth: 0, background: tinted ? 'color-mix(in oklch, var(--accent) 6%, transparent)' : 'transparent', ...style }}>
      {children}
    </div>
  );
};

const Money = ({ v, color, bold, dash }) => (
  <span className="mono" style={{ fontSize: 14, fontWeight: bold ? 700 : 500, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{dash ? '–' : fmt$(v)}</span>
);

const HeadCell = ({ children, align = 'right', col, colHover }) => {
  const tinted = col >= 1 && col <= 5 && col === colHover;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', padding: '0 12px', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', background: tinted ? 'color-mix(in oklch, var(--accent) 7%, var(--bg-surface-2))' : 'var(--bg-surface-2)', whiteSpace: 'nowrap' }}>{children}</div>
  );
};

export const PhaseTable = ({ phases, jobId, getNotes, addNote }) => {
  const [colHover, setColHover] = useState(null);
  const [rowHover, setRowHover] = useState(null);
  const [openNote, setOpenNote] = useState(null); // { phaseNo, code, label }
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const cardRef = useRef(null);

  const rowProps = (id) => ({
    onMouseEnter: () => setRowHover(id),
    onMouseMove: (e) => { const c = e.target.closest('[data-col]'); if (c) setColHover(Number(c.getAttribute('data-col'))); },
  });

  const openNoteAt = (btn, meta) => {
    const r = btn.getBoundingClientRect();
    const cr = cardRef.current.getBoundingClientRect();
    setPos({ top: r.bottom - cr.top + 6, right: cr.right - r.right - 4 });
    setOpenNote(meta);
  };

  const totals = (phases || []).reduce((a, p) => ({ budget: a.budget + p.budget, cost: a.cost + p.actual_cost, committed: a.committed + p.committed, over: a.over + p.over_under }), { budget: 0, cost: 0, committed: 0, over: 0 });
  const totalSpent = totals.budget ? totals.cost / totals.budget : null;
  const ROWH = 44;

  return (
    <div ref={cardRef} style={{ position: 'relative' }} onMouseLeave={() => { setColHover(null); setRowHover(null); }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 880 }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, height: 38, position: 'sticky', top: 0, zIndex: 3, borderBottom: '1px solid var(--border-default)' }}>
              <HeadCell align="left" col={0}>Phase / Cost Code</HeadCell>
              <HeadCell col={1} colHover={colHover}>Budget</HeadCell>
              <HeadCell col={2} colHover={colHover}>Actual Cost</HeadCell>
              <HeadCell col={3} colHover={colHover}>Committed</HeadCell>
              <HeadCell col={4} colHover={colHover}>Over / Under</HeadCell>
              <HeadCell col={5} colHover={colHover}>% Spent</HeadCell>
              <HeadCell col={6} align="left"> </HeadCell>
            </div>

            {(phases || []).map((ph) => {
              const pPct = ph.budget ? ph.actual_cost / ph.budget : null;
              return (
                <React.Fragment key={ph.no}>
                  <div {...rowProps(`ph-${ph.no}`)} style={{ display: 'grid', gridTemplateColumns: GRID, minHeight: ROWH, background: 'color-mix(in oklch, var(--accent) 8%, var(--bg-surface))', borderBottom: '1px solid var(--border-subtle)' }}>
                    <Cell col={0} align="left" colHover={colHover}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, padding: '7px 0' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ph.name}</span>
                          <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>Phase {ph.no} · {ph.codes.length} cost codes</span>
                        </div>
                        <HealthBadge pct={pPct} size="sm" />
                      </div>
                    </Cell>
                    <Cell col={1} colHover={colHover}><Money v={ph.budget} bold /></Cell>
                    <Cell col={2} colHover={colHover}><Money v={ph.actual_cost} bold color={spentColor(pPct)} /></Cell>
                    <Cell col={3} colHover={colHover}><Money v={ph.committed} bold color={ph.committed ? 'var(--warning)' : 'var(--text-4)'} /></Cell>
                    <Cell col={4} colHover={colHover}><Money v={ph.over_under} bold color={overTone(ph.over_under)} /></Cell>
                    <Cell col={5} colHover={colHover}><span className="mono" style={{ fontSize: 14, fontWeight: 700, color: spentColor(pPct), fontVariantNumeric: 'tabular-nums' }}>{pPct == null ? '–' : fmtPct(pPct * 100)}</span></Cell>
                    <Cell col={6} colHover={colHover}> </Cell>
                  </div>

                  {ph.codes.map((cd) => {
                    const cPct = cd.budget ? cd.actual_cost / cd.budget : null;
                    const rid = `c-${ph.no}-${cd.code}`;
                    const notes = getNotes(ph.no, cd.code);
                    const has = notes.length > 0;
                    const isOpen = openNote && openNote.phaseNo === ph.no && openNote.code === cd.code;
                    return (
                      <div key={cd.code} {...rowProps(rid)} style={{ display: 'grid', gridTemplateColumns: GRID, minHeight: ROWH, background: rowHover === rid ? 'var(--bg-hover)' : 'transparent', borderBottom: '1px solid var(--border-subtle)' }}>
                        <Cell col={0} align="left" colHover={colHover}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, paddingLeft: 14 }}>
                            <span style={{ width: 5, height: 5, borderRadius: 99, background: 'var(--border-strong)', flex: '0 0 auto' }} />
                            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cd.cat}</span>
                            {cd.code && <span className="mono" style={{ fontSize: 12, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>{cd.code}</span>}
                            {cd.assumed && <span style={{ display: 'inline-flex', alignItems: 'center', height: 16, padding: '0 6px', borderRadius: 5, flex: '0 0 auto', fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', color: 'var(--accent-text)', background: 'var(--accent-soft)', border: `1px solid ${ring('var(--accent)')}` }}>assumed</span>}
                          </div>
                        </Cell>
                        <Cell col={1} colHover={colHover}><Money v={cd.budget} dash={!cd.budget} /></Cell>
                        <Cell col={2} colHover={colHover}><Money v={cd.actual_cost} color={spentColor(cPct)} /></Cell>
                        <Cell col={3} colHover={colHover}><Money v={cd.committed} color={cd.committed ? 'var(--warning)' : 'var(--text-4)'} dash={!cd.committed} /></Cell>
                        <Cell col={4} colHover={colHover}><Money v={cd.over_under} color={cd.over_under > 0.5 ? 'var(--danger)' : 'var(--text-3)'} /></Cell>
                        <Cell col={5} colHover={colHover}><span className="mono" style={{ fontSize: 14, fontWeight: 600, color: spentColor(cPct), fontVariantNumeric: 'tabular-nums' }}>{cPct == null ? '–' : fmtPct(cPct * 100)}</span></Cell>
                        <Cell col={6} align="center" colHover={colHover}>
                          <NoteIndicator
                            notes={notes}
                            has={has}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isOpen) setOpenNote(null);
                              else openNoteAt(e.currentTarget, { phaseNo: ph.no, code: cd.code, label: `${cd.cat} · ${cd.code}` });
                            }}
                          />
                        </Cell>
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}

            <div style={{ display: 'grid', gridTemplateColumns: GRID, minHeight: 46, borderTop: '2px solid var(--border-default)', background: 'var(--bg-surface-2)' }}>
              <Cell col={0} align="left"><span style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-2)' }}>Total</span></Cell>
              <Cell col={1} colHover={colHover}><Money v={totals.budget} bold /></Cell>
              <Cell col={2} colHover={colHover}><Money v={totals.cost} bold color={spentColor(totalSpent)} /></Cell>
              <Cell col={3} colHover={colHover}><Money v={totals.committed} bold color={totals.committed ? 'var(--warning)' : 'var(--text-4)'} /></Cell>
              <Cell col={4} colHover={colHover}><Money v={totals.over} bold color={overTone(totals.over)} /></Cell>
              <Cell col={5} colHover={colHover}><span className="mono" style={{ fontSize: 14.5, fontWeight: 800, color: spentColor(totalSpent), fontVariantNumeric: 'tabular-nums' }}>{totalSpent == null ? '–' : fmtPct(totalSpent * 100)}</span></Cell>
              <Cell col={6}> </Cell>
            </div>
          </div>
        </div>
      </div>

      {openNote && (
        <NotePopover
          notes={getNotes(openNote.phaseNo, openNote.code)}
          pos={pos}
          codeLabel={openNote.label}
          onAdd={(txt) => addNote(openNote.phaseNo, openNote.code, txt)}
          onClose={() => setOpenNote(null)}
        />
      )}
    </div>
  );
};

/* ─────────────── Budget Utilization bar ─────────────── */

/* Segment-label fit test. The in-bar labels used to be gated on the segment's
   PERCENTAGE of the bar alone, which is width-blind: at the dashboard's narrow
   190px bar column, `commW >= 14` passes at 26.6px while "$1.2M" needs 34.8px, so
   the label rendered clipped to an unreadable stub instead of being dropped. We
   measure the bar and require the segment to be wide enough in ACTUAL pixels too.
   Geist Mono advances 0.600em and letter-spacing is -0.02em, so a 12px label costs
   ~6.96px/char and a 12.5px one ~7.25px/char. */
const labelFitsSegment = (barPx, segPct, text, size, pad) =>
  barPx == null || (segPct / 100) * barPx >= text.length * size * 0.58 + pad;

/* Below this bar width the caption sheds its "$15.3M of $15.1M" token. That token
   is the weakest of the caption's four: the "$15.3M" half is already printed inside
   the bar a few pixels above it, and both halves exist at full cents in the row's
   own Budget/Spent columns. Shedding it is what stops the caption wrapping to four
   and five lines on a laptop (which inflated every job card by 22–46px). The
   project-detail Job Costs tab gives the bar >= 240px, so it keeps the token. */
const CAPTION_TIGHT_PX = 240;

export const BudgetBar = ({ budget, cost, committed, caption = true, height = 24 }) => {
  const denom = Math.max(budget, cost + committed, 1);
  const costW = (cost / denom) * 100;
  const commW = (committed / denom) * 100;
  const budgetX = (budget / denom) * 100;
  const filledW = Math.min(costW + commW, 100);
  const remaining = Math.max(budget - cost - committed, 0);
  const remainW = Math.max(Math.min(budgetX, 100) - filledW, 0);
  const pct = budget ? cost / budget : null;
  const tone = healthTone(pct);
  const over = pct != null && pct > 1.00005;

  // Measure the rendered bar so the label gates above can use real pixels. Until
  // the first measurement lands barPx is null, which makes every gate fall back to
  // the old percentage-only behaviour — never a blank bar on the first paint.
  const barRef = useRef(null);
  const [barPx, setBarPx] = useState(null);
  // useLayoutEffect, not useEffect: measure before the browser paints, so the caption
  // never shows its wide form for a frame and then sheds a token. All 63 bars read in
  // the same layout pass, so this costs one reflow, not 63. The ResizeObserver then
  // keeps it correct as the window changes — which is the whole point of the gate.
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    setBarPx(el.getBoundingClientRect().width || null);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0] && entries[0].contentRect ? entries[0].contentRect.width : 0;
      if (w) setBarPx(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const costTxt = fmt$c(cost);
  const commTxt = fmt$c(committed);
  const remTxt = fmt$c(remaining);
  const showCost = costW >= 22 && labelFitsSegment(barPx, costW, costTxt, 12.5, 10);
  const showComm = commW >= 14 && labelFitsSegment(barPx, commW, commTxt, 12, 8);
  const showRem = remainW >= 20 && labelFitsSegment(barPx, remainW, remTxt, 12, 10);
  const showRemLong = remainW >= 33 && labelFitsSegment(barPx, remainW, remTxt + ' left', 12, 10);
  const captionTight = barPx != null && barPx < CAPTION_TIGHT_PX;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div ref={barRef} style={{ position: 'relative', height, borderRadius: 6, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(costW, 100)}%`, background: over ? 'repeating-linear-gradient(45deg, var(--danger) 0 7px, color-mix(in oklch, var(--danger) 70%, var(--bg-surface)) 7px 14px)' : tone, display: 'flex', alignItems: 'center', paddingLeft: 8, overflow: 'hidden' }}>
          {showCost && <span className="mono" style={{ fontSize: 12.5, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', textShadow: '0 1px 1px oklch(0 0 0 / 0.18)', whiteSpace: 'nowrap' }}>{costTxt}</span>}
        </div>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${Math.min(costW, 100)}%`, width: `${commW}%`, background: 'repeating-linear-gradient(45deg, color-mix(in oklch, var(--warning) 55%, var(--bg-surface-2)) 0 6px, color-mix(in oklch, var(--warning) 32%, var(--bg-surface-2)) 6px 12px)', borderLeft: '1px solid var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {showComm && <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--warning)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{commTxt}</span>}
        </div>
        {/* Remaining budget — an explicit filled segment + label so the headroom
            reads clearly instead of blending into the gray track/background. */}
        {remaining > 0 && remainW > 0 && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${filledW}%`, width: `${remainW}%`, background: 'color-mix(in oklch, var(--text-3) 14%, var(--bg-surface-2))', borderLeft: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8, overflow: 'hidden' }}>
            {/* Never wrap. Drop the " left" suffix when the segment is too narrow
                for the full label (the caption below always spells out "left"). */}
            {showRem && <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{remTxt}{showRemLong ? ' left' : ''}</span>}
          </div>
        )}
        {budgetX < 99.5 && <div style={{ position: 'absolute', top: -1, bottom: -1, left: `${budgetX}%`, width: 0, borderLeft: '2px dashed var(--text-2)', zIndex: 2 }} />}
      </div>
      {caption && (
        <div className="mono" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', flexWrap: 'wrap' }}>
          <span style={{ color: spentColor(pct), fontWeight: 700 }}>{pct == null ? '–' : fmtPct(pct * 100)} spent</span>
          {!captionTight && <><span style={{ color: 'var(--text-4)' }}>·</span><span>{fmt$c(cost)} of {fmt$c(budget)}</span></>}
          {committed > 0 && <><span style={{ color: 'var(--text-4)' }}>·</span><span style={{ color: 'var(--warning)' }}>{fmt$c(committed)} committed</span></>}
          {remaining > 0 && <><span style={{ color: 'var(--text-4)' }}>·</span><span style={{ color: 'var(--text-2)', fontWeight: 600 }}>{fmt$c(remaining)} left</span></>}
        </div>
      )}
    </div>
  );
};

/* ─────────────── Billing %-complete bar ─────────────── */

export const BillingBar = ({ pct, billed, contract, height = 8 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
    <div style={{ height, borderRadius: 999, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.min(pct * 100, 100)}%`, background: 'var(--accent)', borderRadius: 999 }} />
    </div>
    <div className="mono" style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
      <span style={{ color: 'var(--accent-text)', fontWeight: 700 }}>{fmtPct(pct * 100)}</span> · {fmt$c(billed)} of {fmt$c(contract)}
    </div>
  </div>
);

/* ─────────────── % Spent progress ring ─────────────── */

export const SpentRing = ({ pct, size = 64 }) => {
  const tone = spentColor(pct);
  const R = (size - 8) / 2, C = 2 * Math.PI * R;
  const frac = pct == null ? 0 : Math.min(pct, 1);
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke="var(--bg-surface-2)" strokeWidth="5" />
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke={tone} strokeWidth="5" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - frac)} />
      </svg>
      <span className="mono" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.23, fontWeight: 800, color: tone, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.04em' }}>{pct == null ? '–' : `${Math.round(pct * 100)}%`}</span>
    </div>
  );
};

/* ─────────────── synced chip ─────────────── */

export const SyncChip = ({ label = 'Synced from ComputerEase', when = 'just now' }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 30, padding: '0 12px 0 10px', borderRadius: 999, background: 'var(--accent-soft)', border: `1px solid ${ring('var(--accent)')}`, color: 'var(--accent-text)', boxShadow: 'var(--shadow-sm)' }}>
    <span style={{ display: 'inline-flex' }}><JcIcon name="sync" size={14} sw={1.9} /></span>
    <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em' }}>{label}</span>
    <span className="mono" style={{ fontSize: 12.5, color: 'color-mix(in oklch, var(--accent-text) 70%, transparent)', fontVariantNumeric: 'tabular-nums' }}>{when}</span>
  </div>
);

/* ─────────────── shared financials builder ─────────────── */

// totals: { total_budget, total_cost, total_committed } from the CE summary.
// project: the linked project row (revised_contract_value / contract_amount / total_billed).
export const buildFinancials = (totals, project) => {
  const contract_value = parseFloat(project?.revised_contract_value || project?.contract_amount || 0);
  const budget = parseFloat(totals?.total_budget || 0);
  const cost = parseFloat(totals?.total_cost || 0);
  const committed = parseFloat(totals?.total_committed || 0);
  const billed = parseFloat(project?.total_billed || 0);
  const pctSpent = budget > 0 ? cost / budget : null;
  const gm = contract_value - budget;
  const gmPct = contract_value > 0 ? gm / contract_value : 0;
  const actual_gm = contract_value - (cost + committed);
  const actual_gm_pct = contract_value > 0 ? actual_gm / contract_value : 0;
  return {
    contract_value, budget, actual_cost: cost, committed,
    over_under: cost - budget, pctSpent,
    billed, billing_pct: contract_value > 0 ? billed / contract_value : 0,
    gm, gmPct, gm_target: gmPct, actual_gm, actual_gm_pct,
    risk_budget: parseFloat(totals?.risk_budget || 0),
    risk_cost: parseFloat(totals?.risk_cost || 0),
    travel_budget: parseFloat(totals?.travel_budget || 0),
    travel_cost: parseFloat(totals?.travel_cost || 0),
  };
};
